/* eslint-disable @typescript-eslint/no-explicit-any */
import { MaterialCommunityIcons } from '@expo/vector-icons';
import { GroupFilterModel, useCurrentWorkspace } from '@gripp/shared-logic';
import {
  CellContext,
  ColumnDef,
  createColumnHelper,
} from '@tanstack/react-table';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Platform, StyleProp, Text, View } from 'react-native';
import { Colors } from '../../themes/colors';
import { BaseColumnCheckbox } from '../baseColumnCheckbox';
import { themedStyleSheet } from '../../themes/themedStyleSheet';
import { useThemeMode } from '../../themes/useThemeMode';

export type GroupTableData = {
  name: string;
  numReportsRed: string;
  numReportsYellow: string;
  assets: string;
  people: string;
  routines: string;
  assetAlerts: string;
  groupAlerts: string;
};

const IconSize = 16;

const BaseColumns: Record<
  string,
  {
    element: (info: CellContext<GroupTableData, unknown>) => JSX.Element;
    exportCell?: (info: GroupTableData) => string;
  }
> = {
  name: {
    element: (info) => getInfoLabel(styles.nameLabel, info),
  },
  reports: {
    element: (info) => {
      const data = info.row.original;

      return (
        <View style={styles.iconRow}>
          <View style={[styles.iconContainer, { marginRight: 12 }]}>
            <MaterialCommunityIcons
              name="flag"
              color={Colors.red}
              size={IconSize}
              style={styles.icon}
            />
            {getLabel([styles.greyLabel, styles.flagText], data.numReportsRed)}
          </View>

          <View style={styles.iconContainer}>
            <MaterialCommunityIcons
              name="flag-outline"
              color={Colors.yellow}
              size={IconSize}
              style={styles.icon}
            />
            {getLabel(
              [styles.greyLabel, styles.flagText],
              data.numReportsYellow
            )}
          </View>
        </View>
      );
    },
    exportCell: (data) =>
      `Red: ${data.numReportsRed}, Yellow: ${data.numReportsYellow}`,
  },
  assets: {
    element: (info) => {
      const data = info.row.original;

      const AssetsButton = () => {
        return (
          <View style={[styles.iconContainer, { marginRight: 12 }]}>
            <MaterialCommunityIcons
              name="qrcode"
              color={Colors.role.textPrimary}
              size={IconSize}
              style={styles.icon}
            />
            {getLabel(styles.countText, data.assets)}
          </View>
        );
      };

      return <View style={styles.iconRow}>{<AssetsButton />}</View>;
    },
    exportCell: (data) => data.assets,
  },
  people: {
    element: (info) => {
      const data = info.row.original;

      return (
        <View style={styles.iconRow}>
          <View style={[styles.iconContainer, { marginRight: 12 }]}>
            <MaterialCommunityIcons
              name="account-multiple"
              color={Colors.role.textPrimary}
              size={IconSize}
              style={styles.icon}
            />
            {getLabel(styles.countText, data.people)}
          </View>
        </View>
      );
    },
    exportCell: (data) => data.people,
  },
  routines: {
    element: (info) => {
      const data = info.row.original;

      return (
        <View style={styles.iconRow}>
          <View style={[styles.iconContainer, { marginRight: 12 }]}>
            <MaterialCommunityIcons
              name="format-list-checks"
              color={Colors.blue}
              size={IconSize}
              style={styles.icon}
            />
            {getLabel(styles.countText, data.routines)}
          </View>
        </View>
      );
    },
    exportCell: (data) => data.routines,
  },
  assetAlerts: {
    element: (info) => {
      const data = info.row.original;

      return (
        <View style={styles.iconRow}>
          <View style={[styles.iconContainer, { marginRight: 12 }]}>
            <MaterialCommunityIcons
              name="alarm-check"
              color={Colors.role.textPrimary}
              size={IconSize}
              style={styles.icon}
            />
            {getLabel(styles.countText, data.assetAlerts)}
          </View>
        </View>
      );
    },
    exportCell: (data) => data.assetAlerts,
  },
  groupAlerts: {
    element: (info) => {
      const data = info.row.original;

      return (
        <View style={styles.iconRow}>
          <View style={[styles.iconContainer, { marginRight: 12 }]}>
            <MaterialCommunityIcons
              name="alarm-check"
              color={Colors.role.textPrimary}
              size={IconSize}
              style={styles.icon}
            />
            {getLabel(styles.countText, data.groupAlerts)}
          </View>
        </View>
      );
    },
    exportCell: (data) => data.groupAlerts,
  },
};

export const useColumns = (
  filter?: GroupFilterModel,
  customAssetsButton?: (data: GroupTableData) => JSX.Element,
  customPeopleButton?: (data: GroupTableData) => JSX.Element,
  customRoutinesButton?: (data: GroupTableData) => JSX.Element,
  customGroupAlertsButton?: (data: GroupTableData) => JSX.Element
) => {
  const [columns, setColumns] = useState<ColumnDef<unknown, never>[]>([]);

  const { t } = useTranslation();
  const columnHelper = createColumnHelper();
  const workspace = useCurrentWorkspace(true);
  const isWeb = Platform.OS === 'web';
  // The column definitions below close over `styles.*` at build time and are
  // cached in state, only rebuilt when the effect's own deps change - unlike
  // a plain render, a mode flip needs to be an explicit dependency or the
  // grid keeps rendering cells with the mode that was active when this
  // effect last ran. See asset/useColumns.tsx for the same underlying gap.
  const mode = useThemeMode();

  useEffect(() => {
    if (!filter) return;

    const updatedColumns: ColumnDef<unknown, never>[] = [];

    if (isWeb && workspace) {
      updatedColumns.push(
        createCheckboxColumn(
          'selected',
          BaseColumnCheckbox.checkbox,
          BaseColumnCheckbox.checkboxHeader
        )
      );
    }

    const customizedBaseColumns = {
      ...BaseColumns,
      assets: {
        ...BaseColumns.assets,
        element: (info) => {
          const data = info.row.original as GroupTableData;

          return customAssetsButton
            ? customAssetsButton(data)
            : BaseColumns.assets.element(info);
        },
      },
      people: {
        ...BaseColumns.people,
        element: (info) => {
          const data = info.row.original as GroupTableData;
          return customPeopleButton
            ? customPeopleButton(data)
            : BaseColumns.people.element(info);
        },
      },
      routines: {
        ...BaseColumns.routines,
        element: (info) => {
          const data = info.row.original as GroupTableData;
          return customRoutinesButton
            ? customRoutinesButton(data)
            : BaseColumns.routines.element(info);
        },
      },
      groupAlerts: {
        ...BaseColumns.groupAlerts,
        element: (info) => {
          const data = info.row.original as GroupTableData;
          return customGroupAlertsButton
            ? customGroupAlertsButton(data)
            : BaseColumns.groupAlerts.element(info);
        },
      },
    };

    Object.entries(customizedBaseColumns).forEach(([key, cell]) => {
      if (filter.columns?.includes(key)) {
        updatedColumns.push(
          createInfoColumn(key, cell.element, undefined, cell.exportCell, true)
        );
      }
    });

    setColumns(updatedColumns);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filter, workspace, mode]);

  const createCheckboxColumn = (
    accessor: string,
    cell: (info: any) => JSX.Element,
    header: (info: any) => JSX.Element
  ) => {
    return columnHelper.accessor(accessor, {
      cell: cell,
      header: header,
      meta: { style: styles.checkboxCell },
    });
  };

  const createInfoColumn = (
    accessor: string,
    cell: (info: any) => JSX.Element,
    headerOverride?: string,
    exportCell?: (info: GroupTableData) => string,
    sortable = true
  ) => {
    return columnHelper.accessor(accessor, {
      cell: cell,
      header: () => headerOverride ?? t(`groups.list.${accessor}`),
      meta: {
        style: styles.cell,
        sortable: sortable,
        exportCell: exportCell,
      },
    });
  };

  return columns;
};

const getLabel = (labelStyle: StyleProp<any>, label: string) => {
  return <Text style={[styles.cellText, labelStyle]}>{label}</Text>;
};

const getInfoLabel = (labelStyle: StyleProp<any>, info: any) => {
  return getLabel(labelStyle, info.getValue());
};

const styles = themedStyleSheet((role) => ({
  cell: {
    alignItems: 'center',
    justifyContent: 'flex-start',
    overflow: 'hidden',
    paddingVertical: 4,
    ...Platform.select({
      web: {
        height: 50,
        paddingRight: 16,
      },
      default: {
        height: 68,
        paddingRight: 12,
      },
    }),
  },
  checkboxCell: {
    maxWidth: 50,
    flexDirection: 'row',
    justifyContent: 'center',
  },
  nameLabel: {
    color: role.textStrong,
  },
  greyLabel: {
    color: role.textSecondary,
  },
  cellText: {
    fontSize: 16,
    ...Platform.select({
      web: {
        fontWeight: '500',
        flexWrap: 'nowrap',
      },
      default: {
        fontWeight: '400',
        flexWrap: 'wrap',
      },
    }),
    color: role.textStrong,
  },
  iconContainer: {
    flexDirection: 'row',
    justifyContent: 'flex-start',
    alignItems: 'center',
  },
  icon: {
    marginRight: 2,
  },
  iconRow: {
    flexDirection: 'row',
    justifyContent: 'flex-start',
    alignItems: 'center',
    flex: 1,
  },
  flagText: {
    fontSize: 16,
    lineHeight: 16,
    color: role.textStrong,
  },
  countText: {
    fontSize: 16,
    color: role.textStrong,
  },
}));
